import { useCallback, useMemo, useState } from 'react';

import { type ExperimentAllocation, useDelayedExperiment } from '@growtherapy/analytics/client';

import {
  INTAKE_SUMMARY_LAYER_PARAM,
  PROVIDER_INTAKE_REVIEW_LAYER,
  PROVIDER_INTAKE_SUMMARY_DELAYED_EXPERIMENT_CONFIG,
  PROVIDER_INTAKE_SUMMARY_EXPERIMENT,
} from '../../statsig/delayedExperiments';
import { resolveIntakeSummaryExperience } from './resolveIntakeSummaryExperience';
import { useIntakeSummaryExperimentTracking } from './useIntakeSummaryExperimentTracking';

/**
 * Orchestrates the intake summary delayed experiment in telehealth.
 * Allocation and experience are shared with the portal; launch banner UI is not.
 */
export function useIntakeSummaryDelayedExperiment(providerShortId: string) {
  const { recordExperimentAllocation, onStageTransition } = useIntakeSummaryExperimentTracking(providerShortId);
  const [recordedAllocation, setRecordedAllocation] = useState<ExperimentAllocation | null>(null);
  const persistRecordedAllocation = useCallback(
    async (input: Parameters<typeof recordExperimentAllocation>[0]) => {
      const allocation = await recordExperimentAllocation(input);
      setRecordedAllocation(allocation);
      return allocation;
    },
    [recordExperimentAllocation],
  );
  const delayedExperiment = useDelayedExperiment({
    experimentKey: PROVIDER_INTAKE_SUMMARY_EXPERIMENT,
    layer: PROVIDER_INTAKE_REVIEW_LAYER,
    layerParamKey: INTAKE_SUMMARY_LAYER_PARAM,
    isTreatment: (value) => value === 'true',
    dynamicConfigKey: PROVIDER_INTAKE_SUMMARY_DELAYED_EXPERIMENT_CONFIG,
    recordExperimentAllocation: persistRecordedAllocation,
    onStageTransition,
  });
  const experience = useMemo(
    () =>
      resolveIntakeSummaryExperience({
        status: delayedExperiment.status,
        cutoverAt: delayedExperiment.cutoverAt,
        now: new Date(),
        recordedAllocationValue: recordedAllocation?.allocationValue,
      }),
    [delayedExperiment.cutoverAt, delayedExperiment.status, recordedAllocation?.allocationValue],
  );

  return {
    ...delayedExperiment,
    experience,
  };
}
